import { Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { useOnlineStatus } from '@leagueplatform/web-common';
import {
  FullPageError,
  LoadingIndicator,
  ErrorState,
} from '@leagueplatform/web-common-components';
import { LeagueConfig, LeagueProvider } from '@leagueplatform/core';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';

import { initMasonryRegistry } from 'utils/init-masonry-registry';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { config } from 'config';
import { appRoutes } from 'app-routes';
import {
  PatientPortalProvider,
  usePatientPortalContext,
} from 'contexts/patient-portal-provider.context';
import { tenantLocales } from 'locales/tenant-locales';
import { PatientPortalUIConfigOptions } from 'utils/patient-portal-ui-config';
import { TenantIntlProvider } from 'components/tenant-intl-provider/tenant-intl-provider.component';
import { PATIENT_PORTAL_SYSTEM_UUIDS } from 'types/patient-portal-system.types';
import { useFavicon } from 'hooks/use-favicon/use-favicon.hook';

initMasonryRegistry();

const LeagueProviderWrapper = () => {
  const { isOffline, isOnline } = useOnlineStatus();

  useFavicon();

  const patientPortalContext = usePatientPortalContext();
  const patientPortalSystemUUID =
    PATIENT_PORTAL_SYSTEM_UUIDS[patientPortalContext.systemId];

  const patientPortalConfig: LeagueConfig = {
    ...config,
    core: {
      ...config.core,
      auth: {
        ...config.core.auth,
        redirectLoginOptions: {
          acr_values: patientPortalContext.systemId,
        },
      },
      ui: PatientPortalUIConfigOptions[patientPortalContext.systemId].coreUI,
      i18n: {
        locale: 'en-AU',
        strings: tenantLocales[patientPortalContext.systemId],
      },
      customHeaders: {
        'X-League-Custom-Fields': `systemId=${patientPortalSystemUUID}`,
      },
      analytics: {
        ...config.core.analytics,
        customProperties: { brand: patientPortalContext.systemId },
      },
    },
    assets: {
      overrides: {
        ...config.assets?.overrides,
        ...PatientPortalUIConfigOptions[patientPortalContext.systemId].assets
          ?.overrides,
      },
    },
  };

  return (
    <LeagueProvider config={patientPortalConfig}>
      <TenantIntlProvider
        systemId={patientPortalContext.systemId}
        config={patientPortalConfig}
      >
        <ObservabilityErrorBoundary
          fallback={<ErrorState error={null} />}
          errorContext={{
            context: { Context: { location: 'Root App ErrorBoundary' } },
          }}
        >
          <Suspense fallback={<LoadingIndicator />}>
            {isOffline && (
              <FullPageError error={null} route={APP_MANAGED_ROUTES.HOME} />
            )}
            {isOnline && <Outlet />}
          </Suspense>
        </ObservabilityErrorBoundary>
        <ReactQueryDevtools initialIsOpen={false} />
      </TenantIntlProvider>
    </LeagueProvider>
  );
};

const router = createBrowserRouter([
  {
    element: (
      <PatientPortalProvider>
        <LeagueProviderWrapper />
      </PatientPortalProvider>
    ),
    children: appRoutes,
  },
]);

export const App = () => <RouterProvider router={router} />;
